import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { clsx } from 'clsx';

import classes from './CompactText.module.scss';

type Tone = 'info' | 'success' | 'warning' | 'error';

export interface CompactTextProps {
  /** Полный текст. Показываем первые `lines` строк, остальное - по наведению/тапу. */
  children: ReactNode;
  /** Плоский текст для подсказки (title) и скринридера; по умолчанию берётся из DOM. */
  fullText?: string;
  lines?: number;
  className?: string;
  /** Иконка слева (заметка/плашка). */
  tone?: Tone | null;
  as?: 'p' | 'div' | 'span';
  role?: string;
}

const TONE_ICON: Record<Tone, string> = { info: 'i', success: '✓', warning: '!', error: '×' };

/**
 * p7: единый компактный текст заметок/уведомлений в кабинетах.
 * Десктоп: подсказка с полным текстом при наведении (title + фокус).
 * Мобильный: кнопка «ещё» раскрывает текст (тап доступен, aria-expanded).
 */
export const CompactText = ({
  children,
  fullText,
  lines = 2,
  className,
  tone = null,
  as: Tag = 'p',
  role,
}: CompactTextProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(false);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState<string | undefined>(fullText);
  const id = useId();

  const measure = () => {
    const el = ref.current;
    if (!el) return;
    if (!fullText) setTitle(el.textContent?.trim() || undefined);
    if (open) return;
    setOverflow(el.scrollHeight - el.clientHeight > 2);
  };

  useLayoutEffect(measure);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Tag className={clsx(classes.root, tone && classes[tone], className)} role={role}>
      {tone ? (
        <span className={classes.icon} aria-hidden='true'>
          {TONE_ICON[tone]}
        </span>
      ) : null}
      <span className={classes.body}>
        <span
          ref={ref}
          id={id}
          className={clsx(classes.text, !open && classes.clamped)}
          style={{ WebkitLineClamp: open ? undefined : lines }}
          title={overflow && !open ? title : undefined}
          tabIndex={overflow && !open ? 0 : undefined}
        >
          {children}
        </span>
        {overflow || open ? (
          <button
            type='button'
            className={classes.more}
            aria-expanded={open}
            aria-controls={id}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Свернуть' : 'Ещё'}
          </button>
        ) : null}
      </span>
    </Tag>
  );
};
